<script setup lang="ts">
import { ref, onMounted } from "vue";
import { api, errorText } from "../api";
import { activityTitle, activityDetail } from "../activityPresentation";
import RowUpdated from "./RowUpdated.vue";
import { RefreshIcon } from "../icons";
const props = defineProps<{ dataset: string }>();
const events = ref<any[]>([]),
  cursor = ref<string | null>(null),
  category = ref("all"),
  busy = ref(false),
  error = ref("");
async function load(more = false) {
  busy.value = true;
  error.value = "";
  if (!more) {
    events.value = [];
    cursor.value = null;
  }
  try {
    const params = new URLSearchParams({
      category: category.value,
      limit: "30",
    });
    if (more && cursor.value) params.set("cursor", cursor.value);
    const r = await api<any>(`/api/datasets/${props.dataset}/audit?${params}`);
    events.value = more ? [...events.value, ...r.events] : r.events;
    cursor.value = r.nextCursor;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
onMounted(() => load());
</script>
<template>
  <section
    class="dataset-activity"
    aria-label="Datasættets aktivitet"
    :aria-busy="busy"
  >
    <div class="section-heading">
      <div>
        <h2>Aktivitet</h2>
        <p class="muted">
          Hvem ændrede hvad? Hændelser fra de seneste 90 dage.
        </p>
      </div>
      <button class="button secondary" :disabled="busy" @click="load()">
        <RefreshIcon :size="16" />Opdatér
      </button>
    </div>
    <label class="activity-filter"
      >Vis hændelser<select
        v-model="category"
        :disabled="busy"
        @change="load()"
      >
        <option value="all">Alle hændelser</option>
        <option value="record">Rækker</option>
        <option value="dataset">Datasættet</option>
        <option value="schema">Felter</option>
        <option value="view">Dashboardkort</option>
        <option value="import">Import og kilder</option>
      </select></label
    >
    <p v-if="error" role="alert" class="error">
      {{ error }}
      <button class="text-button" :disabled="busy" @click="load()">
        Prøv igen
      </button>
    </p>
    <p v-if="busy && !events.length" class="empty-state" role="status">
      Henter aktivitet…
    </p>
    <div v-else-if="!events.length && !error" class="empty-state">
      <h3>Ingen hændelser i denne periode</h3>
      <p>Ændringer i datasættet vises her, når de sker.</p>
    </div>
    <ol v-else class="activity-list">
      <li v-for="event in events" :key="event.id">
        <div class="activity-event">
          <strong>{{ activityTitle(event.action) }}</strong
          ><span class="muted"
            >{{ event.actor_name
            }}<template v-if="activityDetail(event)">
              · {{ activityDetail(event) }}</template
            ></span
          >
          <details>
            <summary>Hændelsesdetaljer</summary>
            <dl>
              <dt>Hændelse</dt>
              <dd>{{ event.action }}</dd>
              <dt>Reference</dt>
              <dd>{{ event.resource_id }}</dd>
              <template v-if="event.metadata?.revision"
                ><dt>Revision</dt>
                <dd>{{ event.metadata.revision }}</dd></template
              >
            </dl>
          </details>
        </div>
        <RowUpdated :value="event.created_at" />
      </li>
    </ol>
    <button
      v-if="cursor"
      class="button secondary activity-more"
      :disabled="busy"
      @click="load(true)"
    >
      {{ busy ? "Henter…" : "Vis ældre hændelser" }}
    </button>
  </section>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/DatasetActivity.scss"
></style>
